import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  DocumentTextIcon,
  ClipboardDocumentCheckIcon,
  MagnifyingGlassIcon,
  CurrencyDollarIcon,
  ShoppingCartIcon,
  TruckIcon,
  PaperClipIcon,
  ArrowLeftIcon,
  ArrowRightIcon,
  DocumentCheckIcon,
  EyeIcon,
  UserIcon,
  BuildingOfficeIcon,
  CalendarIcon,
  HashtagIcon,
  TrashIcon,
  PlusIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline';
import { useAppData } from '../../contexts/AppDataContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { useCompanySettings } from '../../contexts/CompanySettingsContext';
import { useDocuments } from '../../contexts/DocumentsContext';
import toast from 'react-hot-toast';
import DocumentPreview from './DocumentPreview';

const DocumentCreation = ({ editMode = false, documentToEdit = null, onSave = null, onCancel = null }) => {
  const { t } = useLanguage();
  const { companySettings } = useCompanySettings();
  const { createDocument } = useDocuments();
  const { 
    customers, 
    suppliers, 
    products, 
    fetchAllData, 
    getCustomers, 
    getSuppliers, 
    getProducts 
  } = useAppData();
  const [currentStep, setCurrentStep] = useState(editMode ? 'create' : 'select'); // 'select', 'create', 'preview'
  const [selectedType, setSelectedType] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [formData, setFormData] = useState({
    number: '',
    date: new Date().toISOString().split('T')[0],
    dueDate: '', // Date d'échéance
    clientId: '',
    supplierId: '',
    items: [{ 
      productId: '', 
      description: '', 
      quantity: 1, 
      unitPrice: 0, 
      total: 0 
    }],
    subtotal: 0,
    taxRate: companySettings?.defaultTaxRate || 19,
    taxAmount: 0,
    total: 0,
    notes: '',
    paymentMethod: 'virement', // Mode de paiement
    salesPerson: '', // Commercial
    deliveryDate: '', // Date de livraison
    deliveryAddress: '', // Adresse de livraison
    conditions: '', // Conditions particulières
    validUntil: '', // Valable jusqu'au (pour devis)
    estimatedDelivery: '', // Délai de livraison estimé
    paymentTerms: '30', // Conditions de paiement (en jours)
    issuedBy: '', // Émis par
    approvedBy: '' // Approuvé par
  });

  // Load data on component mount and ensure fresh data
  useEffect(() => {
    const loadAllData = async () => {
      try {
        console.log('Loading document creation data...');
        
        // Fetch all data to ensure latest information
        await fetchAllData();
        
        // Also call individual data fetchers to ensure real-time updates
        if (getCustomers) {
          try {
            await getCustomers();
          } catch (error) {
            console.warn('Error fetching customers:', error);
          }
        }
        
        if (getSuppliers) {
          try {
            await getSuppliers();
          } catch (error) {
            console.warn('Error fetching suppliers:', error);
          }
        }
        
        if (getProducts) {
          try {
            await getProducts();
          } catch (error) {
            console.warn('Error fetching products:', error);
          }
        }
        
        console.log('Document creation data loaded successfully:', {
          customers: customers?.length || 0,
          suppliers: suppliers?.length || 0,
          products: products?.length || 0
        });
        
        // Show success message if data is loaded
        if ((customers?.length > 0) || (suppliers?.length > 0) || (products?.length > 0)) {
          toast.success('Données chargées avec succès');
        }
        
      } catch (error) {
        console.error('Error loading document creation data:', error);
        toast.error('Erreur lors du chargement des données');
      }
    };

    loadAllData();
  }, []);

  // Effect to populate form with existing document data in edit mode
  useEffect(() => {
    if (editMode && documentToEdit) {
      console.log('Loading document for editing:', documentToEdit);
      
      // Map document types to the expected format
      const typeMapping = {
        'invoice': 'invoice',
        'delivery-note': 'delivery-note',
        'purchase-order': 'purchase-order',
        'reception-note': 'reception-note',
        'quote': 'quote'
      };

      const docType = documentTypes.find(type => type.id === typeMapping[documentToEdit.type]);
      setSelectedType(docType || documentTypes[0]);

      setFormData({
        number: documentToEdit.number || '',
        date: documentToEdit.date || new Date().toISOString().split('T')[0],
        dueDate: documentToEdit.dueDate || '',
        clientId: documentToEdit.clientId || '',
        supplierId: documentToEdit.supplierId || '',
        items: documentToEdit.items && documentToEdit.items.length > 0 ? documentToEdit.items : [{
          productId: '',
          description: '',
          quantity: 1,
          unitPrice: 0,
          total: 0
        }],
        subtotal: documentToEdit.subtotal || 0,
        taxRate: documentToEdit.taxRate || companySettings?.defaultTaxRate || 19,
        taxAmount: documentToEdit.taxAmount || 0,
        total: documentToEdit.total || 0,
        notes: documentToEdit.notes || '',
        paymentMethod: documentToEdit.paymentMethod || 'virement',
        salesPerson: documentToEdit.salesPerson || '',
        deliveryDate: documentToEdit.deliveryDate || '',
        deliveryAddress: documentToEdit.deliveryAddress || '',
        conditions: documentToEdit.conditions || '',
        validUntil: documentToEdit.validUntil || '',
        estimatedDelivery: documentToEdit.estimatedDelivery || '',
        paymentTerms: documentToEdit.paymentTerms || '30',
        issuedBy: documentToEdit.issuedBy || '',
        approvedBy: documentToEdit.approvedBy || ''
      });
    }
  }, [editMode, documentToEdit, companySettings]);

  // Update form when data changes - with detailed logging
  useEffect(() => {
    if (customers?.length > 0 || suppliers?.length > 0 || products?.length > 0) {
      console.log('Real-time data updated in document creation:', {
        customers: customers?.length || 0,
        suppliers: suppliers?.length || 0,
        products: products?.length || 0,
        sampleCustomer: customers?.[0]?.name || customers?.[0]?.firstName,
        sampleProduct: products?.[0]?.name
      });
    }
  }, [customers, suppliers, products]);

  // Refresh data when switching document types
  useEffect(() => {
    if (selectedType) {
      console.log('Document type selected:', selectedType.id);
      console.log('Available data for', selectedType.id, ':', {
        dataSource: isSupplierDocument() ? 'suppliers' : 'customers',
        count: getDataSource()?.length || 0
      });
    }
  }, [selectedType]);

  // Generate document number when type is selected
  useEffect(() => {
    if (selectedType && !formData.number) {
      const documentNumber = generateDocumentNumber(selectedType.id);
      setFormData(prev => ({
        ...prev,
        number: documentNumber
      }));
    }
  }, [selectedType]);

  // Recalculate totals when items or taxRate change
  useEffect(() => {
    const subtotal = formData.items.reduce((sum, item) => sum + (item.total || 0), 0);
    const taxAmount = subtotal * (formData.taxRate / 100);
    const total = subtotal + taxAmount;
    
    // Only update if values are different to avoid infinite loops
    if (formData.subtotal !== subtotal || formData.taxAmount !== taxAmount || formData.total !== total) {
      setFormData(prev => ({
        ...prev,
        subtotal,
        taxAmount,
        total
      }));
    }
  }, [formData.items, formData.taxRate]);

  const generateDocumentNumber = (docType) => {
    const prefixMap = {
      'invoice': 'FAC',
      'proforma': 'PRO',
      'quote': 'DEV',
      'quote-request': 'DDV',
      'purchase-order': 'BC',
      'delivery-note': 'BL',
      'reception-note': 'BR',
      'return-slip': 'BRT',
      'stock-sheet': 'FS',
      'material-exit': 'BSM',
      'credit-note': 'AVO',
      'payment-receipt': 'REC',
      'attachment': 'ATT',
      'shipping-slip': 'BE',
      'account-statement': 'RLC',
      'advance-invoice': 'ACO'
    };

    const prefix = prefixMap[docType] || 'DOC';
    const counter = Math.floor(Math.random() * 99) + 1;
    return `${prefix}-${counter.toString().padStart(3, '0')}`;
  };

  // Simplified document categories - only 4 essential document types
  const documentCategories = [
    {
      title: 'VENTES',
      color: '#ef4444',
      documents: [
        {
          id: 'invoice',
          title: 'Créer une facture de vente',
          subtitle: 'Facture client',
          icon: DocumentTextIcon,
          color: '#ef4444',
          description: 'Créez une facture de vente conforme à la réglementation algérienne (TVA 19%)'
        },
        {
          id: 'delivery-note',
          title: 'Créer un bon de livraison',
          subtitle: 'Livraison client',
          icon: TruckIcon,
          color: '#dc2626',
          description: 'Documentez la livraison de marchandises aux clients'
        }
      ]
    },
    {
      title: 'ACHATS',
      color: '#3b82f6',
      documents: [
        {
          id: 'purchase-order',
          title: 'Créer un bon de commande',
          subtitle: 'Commande fournisseur',
          icon: ShoppingCartIcon,
          color: '#3b82f6',
          description: 'Formalisez vos commandes auprès des fournisseurs'
        },
        {
          id: 'reception-note',
          title: 'Créer un bon de réception',
          subtitle: 'Réception fournisseur',
          icon: ClipboardDocumentCheckIcon,
          color: '#1d4ed8',
          description: 'Formalisez la réception de marchandises des fournisseurs'
        }
      ]
    }
  ];

  // Extract all document types from categories for easy access
  const documentTypes = documentCategories.flatMap(category => category.documents);

  const handleDocumentSelect = (document) => {
    setSelectedType(document);
    setCurrentStep('create');
  };

  const handleBack = () => {
    if (currentStep === 'create') {
      setCurrentStep('select');
      setSelectedType(null);
      setFormData({
        number: '',
        date: new Date().toISOString().split('T')[0],
        dueDate: '',
        clientId: '',
        supplierId: '',
        items: [{ 
          productId: '', 
          description: '', 
          quantity: 1, 
          unitPrice: 0, 
          total: 0 
        }],
        subtotal: 0,
        taxRate: companySettings?.defaultTaxRate || 19,
        taxAmount: 0,
        total: 0,
        notes: '',
        paymentMethod: 'virement',
        salesPerson: '',
        deliveryDate: '',
        deliveryAddress: '',
        conditions: '',
        validUntil: '',
        estimatedDelivery: '',
        paymentTerms: '30',
        issuedBy: '',
        approvedBy: ''
      });
    } else if (currentStep === 'preview') {
      setCurrentStep('create');
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => {
      const newData = {
      ...prev,
      [field]: value
      };
      
      // Recalculate totals if taxRate changes
      if (field === 'taxRate') {
        const subtotal = newData.items.reduce((sum, item) => sum + item.total, 0);
        const taxAmount = subtotal * (value / 100);
        const total = subtotal + taxAmount;
        
        return {
          ...newData,
          subtotal,
          taxAmount,
          total
        };
      }
      
      return newData;
    });
  };

  const handleItemChange = (index, field, value) => {
    const updatedItems = [...formData.items];
    updatedItems[index] = {
      ...updatedItems[index],
      [field]: value
    };

    // Recalculate item total
    if (field === 'quantity' || field === 'unitPrice') {
      updatedItems[index].total = updatedItems[index].quantity * updatedItems[index].unitPrice;
    }

    // Auto-fill product details if product is selected
    if (field === 'productId' && value) {
      const product = products.find(p => p.id === value);
      if (product) {
        // Try different price field names
        const productPrice = product.sellingPrice || product.price || product.unitPrice || 0;
        
        updatedItems[index].description = product.name || product.description || '';
        updatedItems[index].unitPrice = productPrice;
        updatedItems[index].total = updatedItems[index].quantity * productPrice;
        
        console.log('Product auto-filled:', {
          name: product.name,
          price: productPrice,
          total: updatedItems[index].total
        });
      }
    }

    setFormData(prev => {
      const subtotal = updatedItems.reduce((sum, item) => sum + item.total, 0);
      const taxAmount = subtotal * (prev.taxRate / 100);
      const total = subtotal + taxAmount;

      return {
        ...prev,
        items: updatedItems,
        subtotal,
        taxAmount,
        total
      };
    });
  };

  const addItem = () => {
    setFormData(prev => ({
      ...prev,
      items: [...prev.items, { 
        productId: '', 
        description: '', 
        quantity: 1, 
        unitPrice: 0, 
        total: 0 
      }]
    }));
  };

  const removeItem = (index) => {
    if (formData.items.length > 1) {
      const updatedItems = formData.items.filter((_, i) => i !== index);
      setFormData(prev => {
        const subtotal = updatedItems.reduce((sum, item) => sum + item.total, 0);
        const taxAmount = subtotal * (prev.taxRate / 100);
        const total = subtotal + taxAmount;

        return {
          ...prev,
          items: updatedItems,
          subtotal,
          taxAmount,
          total
        };
      });
    }
  };

  const handlePreview = () => {
    setCurrentStep('preview');
  };

  const handleSave = async () => {
    if (isSaving) {
      return; // Prevent multiple saves
    }
    
    setIsSaving(true);
    try {
      // Validate required fields
      if (!formData.number) {
        toast.error('Numéro de document requis');
        setIsSaving(false);
        return;
      }

      const isSupplier = isSupplierDocument();
      const contactId = isSupplier ? formData.supplierId : formData.clientId;
      
      if (!contactId) {
        toast.error(`Veuillez sélectionner un ${getContactType()}`);
        setIsSaving(false);
        return;
      }

      if (formData.items.length === 0 || formData.items.every(item => !item.description)) {
        toast.error('Veuillez ajouter au moins un article');
        setIsSaving(false);
        return;
      }

      // Get contact information
      const contact = getDataSource()?.find(c => c.id === contactId);
      console.log('💾 Document save - Contact lookup:', { 
        contactId, 
        contact, 
        isSupplier,
        clientIdFromForm: formData.clientId,
        supplierIdFromForm: formData.supplierId,
        dataSource: getDataSource() 
      });
      const contactName = contact ? 
        (contact.name || `${contact.firstName || ''} ${contact.lastName || ''}`.trim() || 'Contact sans nom') : 
        'Contact inconnu';

      // Prepare document data for API
      const documentData = {
        type: selectedType.id,
        number: formData.number,
        date: formData.date,
        dueDate: formData.dueDate || null,
        clientId: isSupplier ? null : contactId,
        supplierId: isSupplier ? contactId : null,
        contactName: contactName,
        contactEmail: contact?.email || '',
        contactPhone: contact?.phone || '',
        contactAddress: contact?.address || '',
        items: formData.items
          .filter(item => item.description)
          .map(item => ({
            productId: item.productId || null,
            description: item.description,
            quantity: parseInt(item.quantity) || 1,
            unitPrice: parseFloat(item.unitPrice) || 0,
            total: parseFloat(item.total) || (parseFloat(item.unitPrice) * parseInt(item.quantity))
          })),
        subtotal: formData.subtotal,
        taxRate: formData.taxRate,
        taxAmount: formData.taxAmount,
        total: formData.total,
        notes: formData.notes || null,
        paymentMethod: formData.paymentMethod || null,
        salesPerson: formData.salesPerson || null,
        deliveryDate: formData.deliveryDate || null,
        deliveryAddress: formData.deliveryAddress || null,
        conditions: formData.conditions || null,
        validUntil: formData.validUntil || null,
        estimatedDelivery: formData.estimatedDelivery || null,
        paymentTerms: formData.paymentTerms || null,
        issuedBy: formData.issuedBy || null,
        approvedBy: formData.approvedBy || null,
        status: 'draft'
      };

      // Save document via API
      let savedDocument;
      if (editMode && onSave) {
        // In edit mode, use the onSave prop
        await onSave(documentData);
        savedDocument = true; // Assume success if no error thrown
      } else {
        // In create mode, use createDocument
        savedDocument = await createDocument(documentData);
        
        if (!savedDocument) {
          setIsSaving(false);
          return; // Error already handled by createDocument
        }
      }

      // Trigger data refresh in other components
      if (fetchAllData) {
        try {
          await fetchAllData();
        } catch (error) {
          console.warn('Error refreshing data after save:', error);
        }
      }

      toast.success(`${selectedType.title.replace('Créer une ', '').replace('Créer un ', '')} créé(e) avec succès!`);
      
      // Reset form and go back to selection
      setCurrentStep('select');
      setSelectedType(null);
      setFormData({
        number: '',
        date: new Date().toISOString().split('T')[0],
        dueDate: '',
        clientId: '',
        supplierId: '',
        items: [{ 
          productId: '', 
          description: '', 
          quantity: 1, 
          unitPrice: 0, 
          total: 0 
        }],
        subtotal: 0,
        taxRate: companySettings?.defaultTaxRate || 19,
        taxAmount: 0,
        total: 0,
        notes: '',
        paymentMethod: 'virement',
        salesPerson: '',
        deliveryDate: '',
        deliveryAddress: '',
        conditions: '',
        validUntil: '',
        estimatedDelivery: '',
        paymentTerms: '30',
        issuedBy: '',
        approvedBy: ''
      });

    } catch (error) {
      console.error('Error saving document:', error);
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setIsSaving(false);
    }
  };

  const isSupplierDocument = () => {
    return ['purchase-order', 'reception-note', 'quote-request'].includes(selectedType?.id);
  };

  const getDataSource = () => {
    return isSupplierDocument() ? suppliers : customers;
  };

  const getContactType = () => {
    return isSupplierDocument() ? 'fournisseur' : 'client';
  };

  // Render document selection step
  if (currentStep === 'select') {
    return (
      <div className="min-h-screen p-6">
        <div className="max-w-6xl mx-auto">
          {/* Header with Back Button */}
          <div className="flex items-center justify-between mb-12">
            <button
              onClick={editMode && onCancel ? onCancel : () => window.history.back()}
              className="flex items-center gap-2 p-2 bg-white/10 hover:bg-white/20 rounded-xl transition-colors text-white"
            >
              <ArrowLeftIcon className="h-5 w-5" />
              <span className="text-sm font-medium">{editMode ? 'Annuler' : 'Retour'}</span>
            </button>
            
            <div className="text-center flex-1">
                            <h1 className="text-3xl font-bold text-white mb-3">
                  {editMode ? 'Modification de Document' : 'Création de Documents'}
                </h1>
                <p className="text-white/60">
                  {editMode ? 'Modifiez les informations du document' : 'Sélectionnez le type de document que vous souhaitez créer'}
                </p>
            </div>
            
            <div className="w-20"></div> {/* Spacer for balance */}
          </div>

          {/* Document Categories */}
          <div className="space-y-12">
            {documentCategories.map((category, categoryIndex) => (
              <motion.div
                key={category.title}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: categoryIndex * 0.2 }}
              >
                {/* Category Title */}
                <h2 className="text-2xl font-bold text-white mb-6 text-center">
                  {category.title}
                </h2>
                
                {/* Documents Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {category.documents.map((document, docIndex) => {
                    const IconComponent = document.icon;
                    return (
                      <motion.div
                        key={document.id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: (categoryIndex * 0.2) + (docIndex * 0.1) }}
                        onClick={() => handleDocumentSelect(document)}
                        className="bg-white/10 backdrop-blur-lg rounded-2xl p-8 border border-white/20 hover:bg-white/20 transition-all duration-300 cursor-pointer group hover:scale-[1.02]"
                      >
                        <div className="flex items-center gap-6">
                          <div 
                            className="w-20 h-20 rounded-2xl flex items-center justify-center flex-shrink-0"
                            style={{ backgroundColor: `${document.color}20` }}
                          >
                            <IconComponent 
                              className="h-10 w-10" 
                              style={{ color: document.color }}
                            />
                          </div>
                          <div className="flex-1">
                            <h3 className="font-bold text-white text-xl mb-2 group-hover:text-red-300 transition-colors">
                              {document.title.replace('Créer une ', '').replace('Créer un ', '')}
                            </h3>
                            <p className="text-white/70 text-base mb-2">
                              {document.subtitle}
                            </p>
                            <p className="text-white/50 text-sm leading-relaxed">
                              {document.description}
                            </p>
                          </div>
                        </div>
                      </motion.div>
                    );
                  })}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Render document creation form
  if (currentStep === 'create') {
    return (
      <div className="min-h-screen p-6">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-4">
              <button
                onClick={handleBack}
                className="p-2 bg-white/10 hover:bg-white/20 rounded-xl transition-colors"
              >
                <ArrowLeftIcon className="h-5 w-5 text-white" />
              </button>
              <div>
                <h1 className="text-2xl font-bold text-white">{selectedType?.title}</h1>
                <p className="text-white/60">{selectedType?.description}</p>
              </div>
            </div>
            <div className="flex gap-2">
              <button
                onClick={handlePreview}
                className="flex items-center gap-2 px-4 py-2 bg-blue-500/20 text-blue-400 rounded-xl hover:bg-blue-500/30 transition-colors"
              >
                <EyeIcon className="h-4 w-4" />
                Aperçu
              </button>
              <button
                onClick={handleSave}
                disabled={isSaving}
                className={`flex items-center gap-2 px-4 py-2 rounded-xl transition-colors ${
                  isSaving 
                    ? 'bg-gray-500/20 text-gray-400 cursor-not-allowed' 
                    : 'bg-green-500/20 text-green-400 hover:bg-green-500/30'
                }`}
              >
                <DocumentCheckIcon className="h-4 w-4" />
                {isSaving ? 'Sauvegarde...' : (editMode ? 'Mettre à jour' : 'Enregistrer')}
              </button>
            </div>
          </div>

          {/* Form */}
          <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 border border-white/20">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              {/* Document Number */}
              <div>
                <label className="block text-white/80 text-sm font-medium mb-2">
                  Numéro du document
                </label>
                <input
                  type="text"
                  value={formData.number}
                  onChange={(e) => handleInputChange('number', e.target.value)}
                  className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-red-500"
                />
              </div>

              {/* Date */}
              <div>
                <label className="block text-white/80 text-sm font-medium mb-2">
                  Date
                </label>
                <input
                  type="date"
                  value={formData.date}
                  onChange={(e) => handleInputChange('date', e.target.value)}
                  className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-red-500"
                />
              </div>

              {/* Client/Supplier */}
              <div className="md:col-span-2">
                <label className="block text-white/80 text-sm font-medium mb-2">
                  {isSupplierDocument() ? 'Fournisseur' : 'Client'}
                </label>
                <select
                  value={isSupplierDocument() ? formData.supplierId : formData.clientId}
                  onChange={(e) => handleInputChange(isSupplierDocument() ? 'supplierId' : 'clientId', e.target.value)}
                  className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-red-500"
                >
                  <option value="">Sélectionner un {getContactType()}</option>
                  {getDataSource()?.map(contact => (
                    <option key={contact.id} value={contact.id} className="text-gray-900">
                      {contact.name || `${contact.firstName} ${contact.lastName}`} 
                      {contact.company ? ` - ${contact.company}` : ''}
                      {contact.email ? ` (${contact.email})` : ''}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Items */}
            <div className="mb-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-white">Articles</h3>
                <button
                  onClick={addItem}
                  className="flex items-center gap-2 px-3 py-2 bg-green-500/20 text-green-400 rounded-xl hover:bg-green-500/30 transition-colors"
                >
                  <PlusIcon className="h-4 w-4" />
                  Ajouter
                </button>
              </div>

              <div className="space-y-4">
                {formData.items.map((item, index) => (
                  <div key={index} className="bg-white/5 rounded-xl p-4 border border-white/10">
                    <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                      <div>
                        <label className="block text-white/80 text-sm font-medium mb-2">
                          Produit
                        </label>
                        <select
                          value={item.productId}
                          onChange={(e) => handleItemChange(index, 'productId', e.target.value)}
                          className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-red-500"
                        >
                          <option value="">Sélectionner un produit</option>
                          {products?.map(product => (
                            <option key={product.id} value={product.id} className="text-gray-900">
                              {product.name} - {(product.sellingPrice || product.price || 0).toLocaleString()} DA
                            </option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label className="block text-white/80 text-sm font-medium mb-2">
                          Description
                        </label>
                        <input
                          type="text"
                          value={item.description}
                          onChange={(e) => handleItemChange(index, 'description', e.target.value)}
                          className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-red-500"
                        />
                      </div>

                      <div>
                        <label className="block text-white/80 text-sm font-medium mb-2">
                          Quantité
                        </label>
                        <input
                          type="number"
                          value={item.quantity}
                          onChange={(e) => handleItemChange(index, 'quantity', parseFloat(e.target.value) || 0)}
                          className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-red-500"
                        />
                      </div>

                      <div>
                        <label className="block text-white/80 text-sm font-medium mb-2">
                          Prix unitaire
                        </label>
                        <input
                          type="number"
                          value={item.unitPrice}
                          onChange={(e) => handleItemChange(index, 'unitPrice', parseFloat(e.target.value) || 0)}
                          className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-red-500"
                        />
                      </div>

                      <div className="flex items-end gap-2">
                        <div className="flex-1">
                          <label className="block text-white/80 text-sm font-medium mb-2">
                            Total
                          </label>
                          <input
                            type="text"
                            value={item.total.toLocaleString() + ' DA'}
                            readOnly
                            className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-xl text-white"
                          />
                        </div>
                        {formData.items.length > 1 && (
                          <button
                            onClick={() => removeItem(index)}
                            className="p-2 text-red-400 hover:bg-red-400/10 rounded-xl transition-colors"
                          >
                            <TrashIcon className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Totals */}
            <div className="bg-white/5 rounded-xl p-4 border border-white/10 mb-6">
              <div className="flex justify-end">
                <div className="w-64 space-y-2">
                  <div className="flex justify-between text-white/80">
                    <span>Sous-total:</span>
                    <span>{formData.subtotal.toLocaleString()} DA</span>
                  </div>
                  <div className="flex justify-between items-center text-white/80">
                    <span>TVA:</span>
                    <div className="flex items-center gap-2">
                      <input
                        type="number"
                        value={formData.taxRate}
                        onChange={(e) => handleInputChange('taxRate', parseFloat(e.target.value) || 0)}
                        min="0"
                        max="100"
                        step="0.1"
                        className="w-16 px-2 py-1 bg-white/10 border border-white/20 rounded text-white text-sm text-center focus:outline-none focus:ring-1 focus:ring-red-500"
                      />
                      <span className="text-white/60">%</span>
                      <span className="text-white/80 min-w-[80px] text-right">{formData.taxAmount.toLocaleString()} DA</span>
                    </div>
                  </div>
                  <div className="border-t border-white/20 pt-2">
                    <div className="flex justify-between text-white font-semibold text-lg">
                      <span>Total:</span>
                      <span>{formData.total.toLocaleString()} DA</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Document-specific fields */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Payment Method - For invoices and some other docs */}
              {(selectedType?.id === 'invoice' || selectedType?.id === 'purchase-order') && (
                <div>
                  <label className="block text-white/80 text-sm font-medium mb-2">
                    Mode de paiement
                  </label>
                  <select
                    value={formData.paymentMethod}
                    onChange={(e) => handleInputChange('paymentMethod', e.target.value)}
                    className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-red-500"
                  >
                    <option value="virement" className="text-gray-900">Virement bancaire</option>
                    <option value="cheque" className="text-gray-900">Chèque</option>
                    <option value="especes" className="text-gray-900">Espèces</option>
                    <option value="carte" className="text-gray-900">Carte bancaire</option>
                    <option value="effet" className="text-gray-900">Effet de commerce</option>
                    <option value="traite" className="text-gray-900">Traite</option>
                  </select>
                </div>
              )}

              {/* Due Date - For invoices */}
              {selectedType?.id === 'invoice' && (
                <div>
                  <label className="block text-white/80 text-sm font-medium mb-2">
                    Date d'échéance
                  </label>
                  <input
                    type="date"
                    value={formData.dueDate}
                    onChange={(e) => handleInputChange('dueDate', e.target.value)}
                    className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-red-500"
                  />
                </div>
              )}

              {/* Delivery Date - For delivery notes and purchase orders */}
              {(selectedType?.id === 'delivery-note' || selectedType?.id === 'purchase-order') && (
                <div>
                  <label className="block text-white/80 text-sm font-medium mb-2">
                    Date de livraison
                  </label>
                  <input
                    type="date"
                    value={formData.deliveryDate}
                    onChange={(e) => handleInputChange('deliveryDate', e.target.value)}
                    className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-red-500"
                  />
                </div>
              )}

              {/* Sales Person - For invoices and quotes */}
              {(selectedType?.id === 'invoice' || selectedType?.id === 'quote') && (
                <div>
                  <label className="block text-white/80 text-sm font-medium mb-2">
                    Commercial
                  </label>
                  <input
                    type="text"
                    value={formData.salesPerson}
                    onChange={(e) => handleInputChange('salesPerson', e.target.value)}
                    className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-red-500"
                    placeholder="Nom du commercial"
                  />
                </div>
              )}

              {/* Issued By - For purchase orders and reception notes */}
              {(selectedType?.id === 'purchase-order' || selectedType?.id === 'reception-note') && (
                <div>
                  <label className="block text-white/80 text-sm font-medium mb-2">
                    Émis par
                  </label>
                  <input
                    type="text"
                    value={formData.issuedBy}
                    onChange={(e) => handleInputChange('issuedBy', e.target.value)}
                    className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-red-500"
                    placeholder="Nom de l'émetteur"
                  />
                </div>
              )}
            </div>

            {/* Delivery Address - For delivery notes and purchase orders */}
            {(selectedType?.id === 'delivery-note' || selectedType?.id === 'purchase-order') && (
              <div>
                <label className="block text-white/80 text-sm font-medium mb-2">
                  Adresse de livraison
                </label>
                <textarea
                  value={formData.deliveryAddress}
                  onChange={(e) => handleInputChange('deliveryAddress', e.target.value)}
                  rows={2}
                  className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-red-500"
                  placeholder="Adresse complète de livraison"
                />
              </div>
            )}

            {/* Conditions - For all documents */}
            <div>
              <label className="block text-white/80 text-sm font-medium mb-2">
                Conditions particulières
              </label>
              <textarea
                value={formData.conditions}
                onChange={(e) => handleInputChange('conditions', e.target.value)}
                rows={3}
                className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-red-500"
                placeholder="Conditions spécifiques au document"
              />
            </div>

            {/* Notes */}
            <div>
              <label className="block text-white/80 text-sm font-medium mb-2">
                Notes
              </label>
              <textarea
                value={formData.notes}
                onChange={(e) => handleInputChange('notes', e.target.value)}
                rows={4}
                className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-red-500"
                placeholder="Notes additionnelles..."
              />
            </div>


                </div>
        </div>
      </div>
    );
  }

  // Helper function to get selected contact data
  const getSelectedContact = () => {
    const contactId = isSupplierDocument() ? formData.supplierId : formData.clientId;
    const dataSource = getDataSource();
    
    if (!contactId) {
      return null;
    }
    
    // Convert string ID to number if necessary (HTML select returns strings)
    const numericContactId = parseInt(contactId);
    const stringContactId = String(contactId);
    
    // Try both numeric and string comparison
    const contact = dataSource?.find(c => c.id === numericContactId || c.id === stringContactId);
    
    return contact || null;
  };

  // Render preview step
  if (currentStep === 'preview') {
    const selectedContact = getSelectedContact();
    
    return (
      <DocumentPreview
        documentType={selectedType}
        formData={{
          ...formData,
          contactName: selectedContact 
            ? (selectedContact.name || `${selectedContact.firstName || ''} ${selectedContact.lastName || ''}`.trim() || 'Non spécifié')
            : 'Non spécifié',
          contactEmail: selectedContact?.email || '',
          contactPhone: selectedContact?.phone || '',
          contactAddress: selectedContact?.address || ''
        }}
        onBack={handleBack}
        onSave={handleSave}
      />
    );
  }

  return null;
};

export default DocumentCreation;